import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'react-multi-select-component';
// eslint-disable-next-line import/no-cycle
import { Context } from './ContextWrapper';
import * as action from '../Store/SystemMasters.action';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';

function CardItem({ index, item: cardDetails }) {
  const { _id, name, isActive, isEditable } = cardDetails;
  const { updateArray, removeFromArray, formState, setFormValues, setFormState, MasterDeleteAccess, MasterAddEditAccess, identifierType } = useContext(Context);
  const {
    UserDetails: { userInfo },
    SystemMastersDetails: { brandList },
  } = useSelector((state) => state);
  const genId = _id || `_id${index}`;

  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [shouldShowDelete, setShouldShowDelete] = useState(true);
  const [shouldShowConfirmation, setShouldShowConfirmation] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!Object.keys(formState?.values)?.includes(_id)) {
      setFormValues(name, _id);
    }
  }, [_id, name, formState]);

  useEffect(() => {
    if (genId.includes('_id')) {
      setShouldShowDelete(false);
      setShouldShowConfirmation(false);
    } else {
      setShouldShowConfirmation(true);
      setShouldShowDelete(true);
    }
  }, [genId]);

  const handleBlur = (e) => {
    const { value } = e.target;
    updateArray({ name: value }, genId);
  };

  const handleDelete = useCallback(async () => {
    try {
      if (genId?.includes('_id')) {
        removeFromArray(genId);

        setFormValues('', genId);
        return null;
      }

      setIsLoading(true);
      const req = { id: genId, section: 'masters', module: 'productIdentifier', action: 'delete' };
      await dispatch(action.deleteProductIdentifier(req, t));
      await dispatch(action.fetchProductIdentifier({ loggedInUserId: userInfo.userId }, t));
      setIsLoading(false);
      setShowDelete((prev) => !prev);
    } catch (error) {}
  }, [dispatch, genId, removeFromArray, setFormValues, t, userInfo.userId, setFormState]);

  const handleActiveInactive = useCallback(async () => {
    try {
      if (genId?.includes('_id')) {
        return null;
      }
      const obj = {
        ...cardDetails,
        isActive: !isActive,
        title: t('PRODUCT_IDENTIFIER.PRODUCT_IDENTIFIERS'),
        loggedInUserId: userInfo.userId,
        section: 'masters',
        module: 'productIdentifier',
        action: 'addEdit',
      };
      setIsLoading(true);
      await dispatch(action.setActiveInactiveProductIdentifier(obj, t));
      await dispatch(action.fetchProductIdentifier(t));

      setIsLoading(false);
      setShowConfirmation((prev) => !prev);
    } catch (error) {}
  }, [cardDetails, dispatch, genId, isActive, t, userInfo.userId]);

  const deleteAction = shouldShowDelete ? () => setShowDelete((prev) => !prev) : handleDelete;
  const activeAction = () => setShowConfirmation((prev) => !prev);
  const brandOptions = (brandList || []).map((b) => ({
    label: b.name,
    value: b._id,
    isActive: b.isActive,
  }));

  const selectedBrands = brandOptions.filter((opt) => (cardDetails.brandIds || []).includes(opt.value));
  return (
    <>
      <div className="list-container">
        <li>
          <div className="input-container">
            <div className="d-flex">
              <input
                key={index}
                name={genId}
                value={formState?.values?.[genId]}
                onChange={(e) => {
                  const inputValue = e.target.value.trimStart().slice(0, 30);
                  setFormValues(inputValue, e.target.name);
                }}
                placeholder={t('PRODUCT_IDENTIFIER.ENTER_PRODUCT_IDENTIFIER')}
                type="text"
                onBlur={handleBlur}
                disabled={!MasterAddEditAccess || !isEditable}
                autoFocus={_id?.includes('_id')}
              />
            </div>
            {formState.isSubmitted && formState.errors?.[genId] && <p className="text-danger m-0 p-1">{formState.errors[genId]}</p>}

            {isEditable && (
              <div className="product-type-container custom-multiselect">
                <MultiSelect
                  name="brand"
                  key={`brand_${index}`}
                  options={brandOptions?.filter((b) => b.isActive)}
                  value={selectedBrands}
                  className={formState.isSubmitted && formState.brandErrors?.[genId] ? 'is-invalid' : ''}
                  overrideStrings={{
                    selectSomeItems: 'Select Brand',
                  }}
                  onChange={(selected) => {
                    const selectedIds = selected.map((e) => e.value);
                    updateArray({ brandIds: selectedIds }, genId);
                    // Clear brand error when user selects a brand
                    if (selected.length > 0 && formState.brandErrors?.[genId]) {
                      setFormState((prev) => {
                        const newBrandErrors = { ...prev.brandErrors };
                        delete newBrandErrors[genId];
                        return { ...prev, brandErrors: newBrandErrors };
                      });
                    }
                  }}
                  isDisabled={!MasterAddEditAccess || !isEditable}
                />
                {formState.isSubmitted && formState.brandErrors?.[genId] && <p className="text-danger m-0 p-1">{formState.brandErrors[genId]}</p>}
              </div>
            )}
          </div>
          <div className="input-action d-flex">
            {MasterAddEditAccess && isEditable && (
              <CustomTooltip tooltipText="Active/inactive">
                <button className="btn p-0 custom-action mr-0" type="button" aria-label="Active" onClick={activeAction}>
                  <span className={isActive ? 'icon-active f-24' : 'icon-inactive f-24'} />
                </button>
              </CustomTooltip>
            )}
            {MasterDeleteAccess && isEditable && (
              <CustomTooltip tooltipText="Delete">
                <button className="btn custom-action p-0" type="button" aria-label="Delete" onClick={deleteAction}>
                  <span className="icon-Delete f-24" />
                </button>
              </CustomTooltip>
            )}
          </div>
        </li>
      </div>

      {shouldShowDelete && (
        <ConfirmationPopup
          onOpen={showDelete}
          onConfirm={() => handleDelete()}
          title={t('DOCUMENT_TYPE.DELETE_TITLE')}
          message={t('DOCUMENT_TYPE.DELETE_MESSAGE')}
          toggle={() => {
            setShowDelete((prev) => !prev);
          }}
          loading={isLoading}
        />
      )}

      <ConfirmationPopup
        onOpen={showConfirmation}
        onConfirm={() => handleActiveInactive()}
        title={t('DOCUMENT_TYPE.ACTIVE/DEACTIVE')}
        message={t('DOCUMENT_TYPE.CONFIRMATION_MESSAGE')}
        toggle={() => {
          setShowConfirmation((prev) => !prev);
        }}
        loading={isLoading}
      />
    </>
  );
}

export default CardItem;
